/**
 * Converts Spatial Analysis layer envelopes (standard GeoJSON, [lng, lat]) into
 * Atlas map shapes ([lat, lng]) so generated analysis output renders as
 * normal map geometry.
 *
 * Key behaviours:
 * - Flips every coordinate from GeoJSON [lng, lat] to Atlas [lat, lng].
 * - MERGES every generated sub-layer (study area, Points of Interest, transit,
 *   gaps, DRT candidate/review-only…) into a SINGLE Atlas `Layer`
 *   ("Spatial Analysis") with one stable, deterministic numeric layerId derived
 *   from the run id, so the map's layer panel shows one entry instead of one per
 *   set. Re-running an analysis (or each poll) upserts that one layer instead of
 *   stacking duplicates, and cleanup can find it.
 * - Each merged shape is still styled/iconned per its SOURCE sub-layer role and
 *   per-feature properties (`role`/`kind`), so per-POI icons and per-role colours
 *   are preserved exactly as when every set had its own layer.
 *
 * Per-shape tagging (so the single layer can still be filtered/focused by set):
 * - `shape.description` carries the source spatial-layer ROLE (existing
 *   convention, e.g. 'transit_supply'). Used for GTFS transit-supply suppression
 *   at the feature level and for per-role focus.
 * - `shape.id` is prefixed with the SOURCE spatial-layer id
 *   (`<runId>:<role-slug>:f<index>`), so a shape is always traceable back to the
 *   exact sub-layer it came from.
 */

import { Layer, Line, Point, Polygon, Shape } from '../../../context/context';
import {
  SpatialAnalysisLayer,
  SpatialAnalysisLayerRole,
} from '../../../services/spatialAnalysis/types';

/**
 * High base offset so spatial-analysis layerIds never collide with the
 * sequential, small ids the editor allocates for user-created layers.
 */
const SPATIAL_LAYER_ID_BASE = 1_000_000_000;

/** Stable display name for the single merged spatial-analysis map layer. */
export const MERGED_SPATIAL_LAYER_NAME = 'Spatial Analysis';

/** Role tag used on shapes that originate from the transit-supply sub-layer. */
export const TRANSIT_SUPPLY_ROLE: SpatialAnalysisLayerRole = 'transit_supply';

type IconType = Point['icon'];

const ROLE_ICON: Partial<Record<SpatialAnalysisLayerRole, IconType>> = {
  amenity_anchor: 'location',
  transit_supply: 'bus',
  // Transit deserts are polygons (styled via the layer fill below), but if a point
  // feature ever appears for this role it gets a semantic warning marker.
  transit_desert: 'flag',
};

/**
 * Maps a point-of-interest `kind` (derived from OSM amenity/shop tags in
 * backend/services/overpass_service.py) to an Atlas marker icon, so each
 * POI renders with a semantic icon rather than one generic marker. Icon names
 * are drawn from the renderer's IconType vocabulary and mirror the app's
 * existing OSM icon maps (processors/osm.ts, handlers/osmHandlers.ts).
 */
const POI_KIND_ICON: Record<string, IconType> = {
  school: 'school',
  university: 'school',
  college: 'school',
  hospital: 'hospital',
  clinic: 'hospital',
  pharmacy: 'hospital',
  supermarket: 'shopping',
  shopping: 'shopping',
  park: 'star',
};

/** Generic fallback icon for unknown/missing POI kinds. */
const POI_FALLBACK_ICON: IconType = 'location';

/** True if an Atlas layerId was allocated for spatial-analysis output. */
export function isSpatialAnalysisLayerId(layerId: number): boolean {
  // Recognises both the single merged layer id and any legacy per-role ids
  // (all allocated above the same base offset), so cleanup of a prior run that
  // produced multiple layers still works.
  return layerId >= SPATIAL_LAYER_ID_BASE;
}

/** Deterministic djb2 hash → stable positive numeric id for a spatial layer string id. */
export function spatialLayerNumericId(spatialLayerId: string): number {
  let hash = 5381;
  for (let i = 0; i < spatialLayerId.length; i += 1) {
    hash = (hash * 33) ^ spatialLayerId.charCodeAt(i);
  }
  // Keep within a safe positive range above the user-layer offset.
  return SPATIAL_LAYER_ID_BASE + (Math.abs(hash) % 1_000_000_000);
}

/**
 * Derives the run id from a spatial sub-layer id of the form `<runId>:<role-slug>`.
 * Falls back to the whole id when there is no separator.
 */
function runIdFromLayerId(spatialLayerId: string): string {
  const idx = spatialLayerId.indexOf(':');
  return idx === -1 ? spatialLayerId : spatialLayerId.slice(0, idx);
}

/**
 * The single, stable numeric layerId for a run's merged spatial-analysis layer.
 * Derived from `<runId>:spatial_analysis` so every poll/re-run upserts the same
 * Atlas layer (no duplicates) and cleanup can find it.
 */
export function mergedSpatialLayerId(runId: string): number {
  return spatialLayerNumericId(`${runId}:spatial_analysis`);
}

function flipRing(ring: number[][]): [number, number][] {
  // GeoJSON [lng, lat] -> Atlas [lat, lng]
  return ring.map(([lng, lat]) => [lat, lng] as [number, number]);
}

function resolveIcon(layer: SpatialAnalysisLayer, properties: Record<string, unknown>): IconType {
  // Points of interest carry a per-feature `kind` (school, hospital, supermarket…)
  // so each one renders with a semantic icon instead of a single generic marker.
  if (layer.role === 'amenity_anchor') {
    const kind = typeof properties.kind === 'string' ? properties.kind.toLowerCase() : '';
    return POI_KIND_ICON[kind] ?? POI_FALLBACK_ICON;
  }
  return ROLE_ICON[layer.role] ?? 'dot';
}

function buildPointShape(
  layer: SpatialAnalysisLayer,
  numericLayerId: number,
  shapeId: string,
  lng: number,
  lat: number,
  properties: Record<string, unknown>,
): Point {
  return {
    id: shapeId,
    layerId: numericLayerId,
    type: 'point',
    name: typeof properties.name === 'string' ? properties.name : layer.name,
    // Source sub-layer role — the per-shape tag used for suppression + focus.
    description: layer.role,
    colour: layer.style.color,
    icon: resolveIcon(layer, properties),
    isLocked: false,
    coordinates: [lat, lng],
    hidden: false,
  };
}

function buildLineShape(
  layer: SpatialAnalysisLayer,
  numericLayerId: number,
  shapeId: string,
  coordinates: [number, number][],
  properties: Record<string, unknown>,
): Line {
  return {
    id: shapeId,
    layerId: numericLayerId,
    type: 'line',
    name: typeof properties.name === 'string' ? properties.name : layer.name,
    colour: layer.style.color,
    description: layer.role,
    opacity: '1',
    borderColour: layer.style.color,
    borderWidth: layer.style.weight ?? 3,
    isLocked: false,
    coordinates,
  };
}

function buildPolygonShape(
  layer: SpatialAnalysisLayer,
  numericLayerId: number,
  shapeId: string,
  rings: [number, number][][],
  properties: Record<string, unknown>,
): Polygon {
  return {
    id: shapeId,
    layerId: numericLayerId,
    type: 'polygon',
    name: typeof properties.name === 'string' ? properties.name : layer.name,
    colour: layer.style.fillColor ?? layer.style.color,
    description: layer.role,
    opacity: String(layer.style.fillOpacity ?? 0.2),
    borderColour: layer.style.color,
    borderWidth: layer.style.weight ?? 2,
    isLocked: false,
    coordinates: rings,
  };
}

/**
 * Convert a single Spatial Analysis sub-layer's GeoJSON into Atlas shapes,
 * styled/iconned per its source role + per-feature properties. The shapes carry
 * the supplied `numericLayerId` (the merged layer's id) so they all live in one
 * Atlas layer.
 */
function buildShapesForLayer(layer: SpatialAnalysisLayer, numericLayerId: number): Shape[] {
  const shapes: Shape[] = [];
  const features = layer.geojson?.features ?? [];

  features.forEach((feature, featureIndex) => {
    const geometry = feature?.geometry;
    if (!geometry) {
      return;
    }
    const properties = (feature.properties ?? {}) as Record<string, unknown>;
    // Prefix with the SOURCE spatial-layer id so a shape stays traceable to its
    // sub-layer even though it now lives in the merged layer.
    const baseId = `${layer.id}:f${featureIndex}`;

    switch (geometry.type) {
      case 'Point': {
        const [lng, lat] = geometry.coordinates as number[];
        shapes.push(buildPointShape(layer, numericLayerId, baseId, lng, lat, properties));
        break;
      }
      case 'MultiPoint': {
        (geometry.coordinates as number[][]).forEach((coord, j) => {
          const [lng, lat] = coord;
          shapes.push(buildPointShape(layer, numericLayerId, `${baseId}:${j}`, lng, lat, properties));
        });
        break;
      }
      case 'LineString': {
        shapes.push(
          buildLineShape(layer, numericLayerId, baseId, flipRing(geometry.coordinates as number[][]), properties),
        );
        break;
      }
      case 'MultiLineString': {
        (geometry.coordinates as number[][][]).forEach((line, j) => {
          shapes.push(buildLineShape(layer, numericLayerId, `${baseId}:${j}`, flipRing(line), properties));
        });
        break;
      }
      case 'Polygon': {
        const rings = (geometry.coordinates as number[][][]).map(flipRing);
        shapes.push(buildPolygonShape(layer, numericLayerId, baseId, rings, properties));
        break;
      }
      case 'MultiPolygon': {
        (geometry.coordinates as number[][][][]).forEach((polygon, j) => {
          const rings = polygon.map(flipRing);
          shapes.push(buildPolygonShape(layer, numericLayerId, `${baseId}:${j}`, rings, properties));
        });
        break;
      }
      default:
        break;
    }
  });

  return shapes;
}

/**
 * Merge ALL of a run's generated sub-layers into a SINGLE Atlas Layer.
 *
 * Every sub-layer's shapes are concatenated into one layer with a stable
 * `mergedSpatialLayerId(runId)` and the display name "Spatial Analysis". Each
 * shape keeps its source role (in `description`) and source-layer-prefixed id, so
 * the merged layer can still be filtered (transit-supply suppression) and focused
 * by sub-layer. The layer is visible unless EVERY contributing sub-layer was
 * `visibleByDefault: false`.
 *
 * Returns an array (0 or 1 layers) so it remains a drop-in for the previous
 * one-layer-per-set API and existing array consumers.
 */
export function convertSpatialLayersToMapLayers(layers: SpatialAnalysisLayer[]): Layer[] {
  if (layers.length === 0) {
    return [];
  }

  const runId = runIdFromLayerId(layers[0].id);
  const numericLayerId = mergedSpatialLayerId(runId);

  const shapes: Shape[] = [];
  layers.forEach((layer) => {
    shapes.push(...buildShapesForLayer(layer, numericLayerId));
  });

  // Visible if any contributing sub-layer was visible by default.
  const hidden = !layers.some((layer) => layer.visibleByDefault);

  return [
    {
      layerId: numericLayerId,
      layerName: MERGED_SPATIAL_LAYER_NAME,
      hidden,
      shapes,
    },
  ];
}

/**
 * Numeric layerId(s) for a run's merged spatial-analysis layer — used for
 * cleanup/reconciliation. Returns the single merged id (derived from the run id
 * shared by all sub-layers).
 */
export function spatialLayerNumericIds(layers: SpatialAnalysisLayer[]): number[] {
  if (layers.length === 0) {
    return [];
  }
  return [mergedSpatialLayerId(runIdFromLayerId(layers[0].id))];
}
